import { useEffect, useState } from 'react';
import { liveAdminApi } from '../lib/api';

/** Salles et séances Live : création, état, occupants du poste d'enseignant. */
export function LiveAdminPage() {
  const [rooms, setRooms] = useState<any[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);
  const [presenters, setPresenters] = useState<any[]>([]);
  const [turns, setTurns] = useState<any[]>([]);
  const [room, setRoom] = useState('');
  const [session, setSession] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => { reload(); }, []);
  useEffect(() => { if (room) liveAdminApi.sessions(room).then(setSessions).catch(() => setSessions([])); }, [room]);
  useEffect(() => {
    if (!session) return;
    liveAdminApi.presenters(session).then(setPresenters).catch(() => setPresenters([]));
    liveAdminApi.turns(session).then(setTurns).catch(() => setTurns([]));
  }, [session]);

  async function reload() {
    try { setRooms(await liveAdminApi.rooms()); }
    catch (e: any) { setError(String(e.message || e)); }
  }

  async function run(fn: () => Promise<any>, note: string) {
    setError(''); setNotice('');
    try { await fn(); setNotice(note); await reload(); if (room) setSessions(await liveAdminApi.sessions(room)); }
    catch (e: any) { setError(String(e.message || e)); }
  }

  const ask = (l: string, d = '') => window.prompt(l, d) ?? '';

  return <section className="page">
    <header className="page-header">
      <div>
        <span className="eyebrow">LIVE</span>
        <h1>Salles et séances</h1>
        <p>Créez les salles, ouvrez les séances, suivez les prises de parole.</p>
      </div>
      <span className="status ok">{rooms.length} salle(s)</span>
    </header>

    {error && <div className="alert error">{error}</div>}
    {notice && <div className="notice">{notice}</div>}

    <div className="card">
      <h3>Salles</h3>
      <button className="button secondary" onClick={() => {
        const code = ask('Code de la salle'); if (!code) return;
        const title = ask('Intitulé') || code;
        run(() => liveAdminApi.createRoom({ code, title }), 'Salle créée.');
      }}>Créer une salle</button>
      <label>Salle
        <select value={room} onChange={(e: any) => { setRoom(e.target.value); setSession(''); }}>
          <option value="">—</option>
          {rooms.map(r => <option key={r.id} value={r.id}>{r.code} — {r.title}</option>)}
        </select>
      </label>
    </div>

    {room && <div className="card">
      <h3>Séances</h3>
      <button className="button secondary" onClick={() => run(() => liveAdminApi.createSession(room), 'Séance ouverte.')}>Ouvrir une séance</button>
      {sessions.map(s => <div key={s.id} className="order-row">
        <strong>{s.id.slice(0, 8)}</strong>
        <span>{s.status}</span>
        <div className="toolbar">
          <button className="button secondary" onClick={() => setSession(s.id)}>Détail</button>
          {s.status !== 'LIVE' && <button className="button primary" onClick={() => run(() => liveAdminApi.setState(s.id, 'LIVE'), 'Séance démarrée.')}>Démarrer</button>}
          {s.status === 'LIVE' && <button className="button secondary" onClick={() => run(() => liveAdminApi.setState(s.id, 'ENDED'), 'Séance close.')}>Terminer</button>}
        </div>
      </div>)}
      {!sessions.length && <p className="technical-note">Aucune séance.</p>}
    </div>}

    {session && <div className="grid two">
      <div className="card">
        <h3>Poste d'enseignant</h3>
        {presenters.map(p => <div key={p.id} className="order-row">
          <strong>{p.display_name || p.kind}</strong>
          <span>{p.kind === 'AI_AVATAR' ? 'avatar' : 'humain'} · {p.seat}{p.is_speaking ? ' · parle' : ''}</span>
          <button className="button secondary" onClick={() => {
            const seat = ask('Emplacement (DESK_LEFT, DESK_RIGHT, TABLE_EDGE, STANDING, PODIUM)', p.seat);
            if (seat) run(() => liveAdminApi.moveSeat(p.id, seat), 'Emplacement modifié.');
          }}>Déplacer</button>
        </div>)}
        {!presenters.length && <p className="technical-note">Poste non installé.</p>}
      </div>

      <div className="card">
        <h3>Prises de parole ({turns.length})</h3>
        {turns.slice(-15).map(t => <div key={t.id} className="order-row">
          <strong>{(t.presenter_id || '').slice(0, 8)}</strong>
          <span>{t.duration_seconds}s {t.ended_at ? '' : '· en cours'}</span>
        </div>)}
        {!turns.length && <p className="technical-note">Aucune prise de parole enregistrée.</p>}
      </div>
    </div>}
  </section>;
}
